import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import {
  ScanLine,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Camera,
  ShieldCheck,
  User,
  Calendar,
  Ticket,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import PortalShell from "@/components/PortalShell";
import { portalApi, portalMe } from "@/lib/portalApi";

const AUDIENCE_LABEL = {
  everyone: "Everyone",
  couples: "Couples",
  tv: "TV",
  single_guys: "Single Guys",
  single_ladies: "Single Ladies",
  single_guy: "Single Man",
  single_lady: "Single Lady",
  couple: "Couple",
};

const fmtWhen = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const READER_ID = "portal-qr-reader";

export default function PortalScan() {
  const [me, setMe] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [starting, setStarting] = useState(false);
  const [result, setResult] = useState(null); // { kind: 'success'|'already'|'error', ...ticketData, error? }
  const [busy, setBusy] = useState(false);
  const scannerRef = useRef(null);
  const lastScanRef = useRef({ value: "", at: 0 });

  useEffect(() => {
    portalMe().then(setMe).catch(() => {});
    return () => {
      stopScanner();
    };
  }, []);

  const stopScanner = async () => {
    try {
      if (scannerRef.current) {
        if (scannerRef.current.getState() === 2 /* SCANNING */) {
          await scannerRef.current.stop();
        }
        await scannerRef.current.clear();
      }
    } catch {
      // ignore
    } finally {
      scannerRef.current = null;
      setScanning(false);
    }
  };

  const handleScan = async (decoded) => {
    // Debounce identical/near-duplicate scans within 3s
    const now = Date.now();
    if (
      lastScanRef.current.value === decoded &&
      now - lastScanRef.current.at < 3000
    ) {
      return;
    }
    lastScanRef.current = { value: decoded, at: now };

    setBusy(true);
    try {
      const { data } = await portalApi.post("/portal/scan/lookup", {
        scanned_value: decoded,
      });
      setResult({
        kind: data.already ? "already" : "success",
        ...data,
      });
      // Optional: pause briefly then keep scanning
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Couldn't check in that ticket.";
      setResult({ kind: "error", error: detail });
    } finally {
      setBusy(false);
    }
  };

  const startScanner = async () => {
    setResult(null);
    setStarting(true);
    try {
      const scanner = new Html5Qrcode(READER_ID, {
        formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
        verbose: false,
      });
      scannerRef.current = scanner;
      const config = {
        fps: 12,
        qrbox: (viewfinderWidth, viewfinderHeight) => {
          const size = Math.floor(Math.min(viewfinderWidth, viewfinderHeight) * 0.75);
          return { width: size, height: size };
        },
        aspectRatio: 1.3,
      };
      await scanner.start(
        { facingMode: "environment" },
        config,
        handleScan,
        () => {
          // Silent decode error — html5-qrcode fires this every frame
        }
      );
      setScanning(true);
    } catch (err) {
      const msg =
        err?.message ||
        (typeof err === "string" ? err : "Could not access the camera.");
      toast.error(
        msg.toLowerCase().includes("permission")
          ? "Camera permission denied. Please allow camera access and try again."
          : msg
      );
      await stopScanner();
    } finally {
      setStarting(false);
    }
  };

  const scanAgain = async () => {
    setResult(null);
    if (!scanning) {
      await startScanner();
    }
  };

  const resetLastScan = () => {
    lastScanRef.current = { value: "", at: 0 };
  };

  return (
    <PortalShell me={me} testId="portal-scan-page">
      <div className="max-w-[900px] mx-auto px-6 md:px-8 py-10">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.28em] text-[#D9A6F5] mb-3 flex items-center gap-2">
            <ScanLine size={14} /> Door check-in
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter">
            QR Scanner
          </h1>
          <p className="mt-3 text-sm text-[#A3A3A3] max-w-xl leading-relaxed">
            Point your phone or laptop camera at a ticket&apos;s QR code. We&apos;ll mark
            it checked in on the guest list for that event so you know exactly
            who walked through the door.
          </p>
        </div>

        {/* Scanner viewport */}
        <div
          className="relative bg-black border border-white/10 overflow-hidden"
          data-testid="scan-viewport"
        >
          <div
            id={READER_ID}
            data-testid="scan-video-holder"
            className="w-full aspect-square md:aspect-[4/3] bg-black flex items-center justify-center text-[#A3A3A3]"
          >
            {!scanning && !starting && (
              <div className="text-center px-6">
                <Camera size={32} className="text-[#D9A6F5] mx-auto mb-4" />
                <div className="text-xs uppercase tracking-[0.28em] text-[#D9A6F5] mb-2">
                  Camera idle
                </div>
                <p className="text-sm text-[#A3A3A3] max-w-sm mx-auto">
                  Tap start to open your camera. Works best in a well-lit
                  entrance.
                </p>
              </div>
            )}
            {starting && (
              <div className="text-center">
                <Loader2 size={26} className="animate-spin text-[#D9A6F5] mx-auto mb-3" />
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#A3A3A3]">
                  Requesting camera…
                </div>
              </div>
            )}
          </div>

          {busy && (
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center">
              <div className="text-center">
                <Loader2 size={24} className="animate-spin text-[#D9A6F5] mx-auto mb-2" />
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#D9A6F5]">
                  Verifying ticket…
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="mt-5 flex flex-wrap gap-3">
          {!scanning ? (
            <button
              type="button"
              onClick={startScanner}
              disabled={starting}
              data-testid="scan-start-button"
              className="inline-flex items-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] disabled:opacity-40 px-6 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
            >
              <Camera size={13} /> Start camera
            </button>
          ) : (
            <button
              type="button"
              onClick={stopScanner}
              data-testid="scan-stop-button"
              className="inline-flex items-center gap-2 border border-white/20 hover:border-red-400 hover:text-red-400 text-[#F5F5F0] px-6 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
            >
              Stop camera
            </button>
          )}
          {result && (
            <button
              type="button"
              onClick={() => {
                resetLastScan();
                setResult(null);
              }}
              data-testid="scan-clear-result"
              className="inline-flex items-center gap-2 border border-white/20 hover:border-[#D9A6F5] hover:text-[#D9A6F5] text-[#F5F5F0] px-6 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
            >
              <RefreshCw size={13} /> Scan next
            </button>
          )}
        </div>

        {/* Result card */}
        {result && (
          <div
            className={`mt-8 border p-6 md:p-7 ${
              result.kind === "success"
                ? "border-[#D9A6F5] bg-[#D9A6F5]/5"
                : result.kind === "already"
                ? "border-amber-400/50 bg-amber-400/5"
                : "border-red-500/60 bg-red-500/5"
            }`}
            data-testid={`scan-result-${result.kind}`}
          >
            {result.kind === "success" && (
              <SuccessCard result={result} onNext={scanAgain} />
            )}
            {result.kind === "already" && (
              <AlreadyCard result={result} onNext={scanAgain} />
            )}
            {result.kind === "error" && (
              <ErrorCard error={result.error} onNext={scanAgain} />
            )}
          </div>
        )}

        {/* Info footer */}
        <div className="mt-10 text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3] inline-flex items-center gap-2">
          <ShieldCheck size={11} className="text-[#D9A6F5]" />
          Each scan is validated server-side. Checked-in tickets show as
          &ldquo;Checked in&rdquo; on the guest list for that event.
        </div>
      </div>
    </PortalShell>
  );
}

function SuccessCard({ result, onNext }) {
  const a = result.attendee || {};
  const e = result.event || {};
  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <CheckCircle2 size={26} className="text-[#D9A6F5]" />
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#D9A6F5]">
            Checked in
          </div>
          <div className="text-2xl md:text-3xl font-black tracking-tighter text-[#F5F5F0] leading-tight">
            Welcome, {a.first_name || "Guest"}.
          </div>
        </div>
      </div>
      <AttendeeGrid attendee={a} event={e} usedAt={result.used_at} />
      <button
        type="button"
        onClick={onNext}
        data-testid="scan-next-button"
        className="mt-6 w-full md:w-auto inline-flex items-center justify-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] px-6 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
      >
        Scan next guest →
      </button>
    </div>
  );
}

function AlreadyCard({ result, onNext }) {
  const a = result.attendee || {};
  const e = result.event || {};
  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <AlertTriangle size={26} className="text-amber-400" />
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-amber-400">
            Already checked in
          </div>
          <div className="text-2xl md:text-3xl font-black tracking-tighter text-[#F5F5F0] leading-tight">
            {a.first_name || "This guest"} was scanned already.
          </div>
          <div className="mt-1 text-[11px] uppercase tracking-[0.22em] text-[#A3A3A3]">
            First seen: {fmtWhen(result.used_at)}
          </div>
        </div>
      </div>
      <AttendeeGrid attendee={a} event={e} usedAt={result.used_at} />
      <button
        type="button"
        onClick={onNext}
        data-testid="scan-next-button"
        className="mt-6 w-full md:w-auto inline-flex items-center justify-center gap-2 border border-white/20 hover:border-[#D9A6F5] hover:text-[#D9A6F5] text-[#F5F5F0] px-6 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
      >
        Scan next guest →
      </button>
    </div>
  );
}

function ErrorCard({ error, onNext }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <XCircle size={26} className="text-red-400" />
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-red-400">
            Ticket rejected
          </div>
          <div className="text-xl md:text-2xl font-black tracking-tighter text-[#F5F5F0]">
            {error}
          </div>
        </div>
      </div>
      <p className="text-sm text-[#A3A3A3] leading-relaxed">
        Try again or ask the guest to open their ticket email and show the QR
        directly.
      </p>
      <button
        type="button"
        onClick={onNext}
        data-testid="scan-next-button"
        className="mt-6 inline-flex items-center gap-2 border border-white/20 hover:border-[#D9A6F5] hover:text-[#D9A6F5] text-[#F5F5F0] px-6 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
      >
        <RefreshCw size={13} /> Try another
      </button>
    </div>
  );
}

function AttendeeGrid({ attendee, event, usedAt }) {
  const fullName =
    `${attendee.first_name || ""} ${attendee.last_name || ""}`.trim() || "—";
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
      <InfoTile
        icon={<User size={14} className="text-[#D9A6F5]" />}
        label="Guest"
        value={fullName}
        sub={attendee.email}
      />
      <InfoTile
        icon={<Ticket size={14} className="text-[#D9A6F5]" />}
        label="Tier"
        value={AUDIENCE_LABEL[attendee.tier] || attendee.tier || "—"}
        sub={
          attendee.membership_number
            ? `Member · ${attendee.membership_number}`
            : null
        }
      />
      <InfoTile
        icon={<Calendar size={14} className="text-[#D9A6F5]" />}
        label="Event"
        value={event.name || "—"}
        sub={event.date ? `${event.date} · ${event.time || ""}` : null}
      />
      <InfoTile
        icon={<CheckCircle2 size={14} className="text-[#D9A6F5]" />}
        label="Checked in"
        value={fmtWhen(usedAt)}
      />
    </div>
  );
}

function InfoTile({ icon, label, value, sub }) {
  return (
    <div className="border border-white/10 p-3">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3] flex items-center gap-1.5 mb-1">
        {icon}
        {label}
      </div>
      <div className="text-[#F5F5F0] font-bold truncate">{value}</div>
      {sub && (
        <div className="mt-0.5 text-[11px] text-[#A3A3A3] truncate">{sub}</div>
      )}
    </div>
  );
}
